/**
 * OpenAlex Direct-Retrieval service client.
 * Wraps /api/openalex-retrieval/* endpoints. Mirrors opsRetrievalService for the
 * scientific-literature source (metadata + abstract only, single retrieval mode).
 */

import apiClient from './api';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface OpenAlexQuery {
  keywords_title_abstract?: string;
  keywords_title?: string;
  keywords_fulltext?: string;
  author?: string[];
  topics?: string[];
}

export interface OpenAlexPreviewSample {
  work_id: string;
  title: string;
  authors?: string[];
  year?: number | null;
  journal?: string;
  cited_by_count?: number;
  abstract_excerpt?: string;
}

/** Result from the /preview/count endpoint (no samples). */
export interface OpenAlexPreviewCountResult {
  total_count: number;
  effective_count: number;
  filter: string;
  term_count: number;
  warnings: string[];
  estimated_seconds: number;
}

/** Full preview result (with samples) — assembled client-side. */
export interface OpenAlexPreviewResult extends OpenAlexPreviewCountResult {
  sample: OpenAlexPreviewSample[];
}

export interface OpenAlexJobProgress {
  total_works: number;
  processed_works: number;
  failed_works: number;
}

export interface OpenAlexRetrievalJob {
  id: string;
  search_session_id: string;
  sub_project_id: string;
  user_id: string;
  target_kb_id: string | null;
  filter: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'cancelling';
  progress: OpenAlexJobProgress;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  error_details: string | null;
  estimated_duration_seconds: number;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const openalexRetrievalService = {
  /** Fast count-only preview (instant on OpenAlex). */
  async previewCount(query: OpenAlexQuery): Promise<OpenAlexPreviewCountResult> {
    const response = await apiClient.post<OpenAlexPreviewCountResult>(
      '/openalex-retrieval/preview/count',
      { query },
      { timeout: 25000 },
    );
    return response.data;
  },

  /**
   * Stream sample works via SSE. Calls onSample for each sample as it arrives.
   * Returns an AbortController so the caller can cancel the stream.
   */
  streamSamples(
    filter: string,
    count: number,
    onSample: (sample: OpenAlexPreviewSample) => void,
    onDone: () => void,
    onError: (message: string) => void,
  ): AbortController {
    const controller = new AbortController();
    const token = sessionStorage.getItem('access_token');
    const baseURL = apiClient.defaults.baseURL || '/api';

    const timeoutId = setTimeout(() => {
      controller.abort();
      onError('Sample loading timed out (30s)');
    }, 30000);

    (async () => {
      try {
        const response = await fetch(`${baseURL}/openalex-retrieval/preview/samples`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ filter, count }),
          signal: controller.signal,
        });

        if (!response.ok) {
          clearTimeout(timeoutId);
          onError(`Server error: ${response.status}`);
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          clearTimeout(timeoutId);
          onError('No response body');
          return;
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let currentEvent = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              const data = line.slice(6);
              try {
                const parsed = JSON.parse(data);
                if (currentEvent === 'sample') {
                  onSample(parsed as OpenAlexPreviewSample);
                } else if (currentEvent === 'done') {
                  clearTimeout(timeoutId);
                  onDone();
                  return;
                } else if (currentEvent === 'error') {
                  clearTimeout(timeoutId);
                  onError(parsed.message || 'Unknown error');
                  return;
                }
              } catch {
                // Skip malformed JSON
              }
              currentEvent = '';
            }
          }
        }

        clearTimeout(timeoutId);
        onDone();
      } catch (err: unknown) {
        clearTimeout(timeoutId);
        const e = err as { name?: string; message?: string };
        if (e.name === 'AbortError') return;
        onError(e?.message || 'Stream failed');
      }
    })();

    return controller;
  },

  /** Create and start a background retrieval job. */
  async startJob(params: {
    sub_project_id: string;
    query: OpenAlexQuery;
    target_kb_id?: string;
    new_kb_name?: string;
    filter?: string;
    total_count?: number;
    max_works?: number | null;
  }): Promise<OpenAlexRetrievalJob> {
    const response = await apiClient.post<OpenAlexRetrievalJob>('/openalex-retrieval/jobs', params);
    return response.data;
  },

  /** Poll job status. */
  async getJobStatus(jobId: string): Promise<OpenAlexRetrievalJob> {
    const response = await apiClient.get<OpenAlexRetrievalJob>(`/openalex-retrieval/jobs/${jobId}`);
    return response.data;
  },

  /** Request cooperative cancellation. */
  async cancelJob(jobId: string): Promise<void> {
    await apiClient.post(`/openalex-retrieval/jobs/${jobId}/cancel`);
  },

  /** List recent jobs for a sub-project. */
  async listJobs(subProjectId: string): Promise<OpenAlexRetrievalJob[]> {
    const response = await apiClient.get<{ jobs: OpenAlexRetrievalJob[] }>(
      `/openalex-retrieval/sub-projects/${subProjectId}/jobs`,
    );
    return response.data.jobs;
  },
};
